<template>
  <div>
    <section class="d-sm-none mb-8">
      <div class=" text-center mt-3">
        <v-btn
          color="primary700 primary200--text"
          class="rounded-0" min-width="100"
          depressed :ripple="false"
          @click="onLogout"
        >
          <fai
            :icon="['fad', 'sign-out']" fixed-width
            class="text-body-1 mr-2"
          />
          Log out
        </v-btn>
      </div>
    </section>

    <!-- levels -->
    <section class="mb-6">
      <v-alert
        v-if="depositConfig.xpMultiplier > 1" dense color="blue800" tile
        class="xp-bonus-banner text-body-2"
      >
        <div v-if="!depositXpBonusActive" class="d-flex align-center">
          <fai :icon="['fad', 'gift']" class="text-h4 mr-4 blue100--text" />
          <div>
            <h4 class="blue200--text text-body-1 font-weight-bold text-uppercase">
              +{{ ( depositConfig.xpMultiplier - 1) * 100 | toLocale }}% XP bonus
            </h4>
            <p class="blue100--text mb-0">
              Have at least <scrap :amount="depositConfig.xpBonusThresholdAmount" /> deposited
              in the last {{ depositConfig.xpBonusThresholdDays }} days to qualify.
            </p>
          </div>
        </div>
        <div v-else>
          <h4 class="d-flex align-center">
            <fai :icon="['fad', 'gift']" class="text-h6 mr-3 blue100--text" />
            <span class="blue200--text text-body-2 font-weight-semibold text-uppercase">
              +{{ ( depositConfig.xpMultiplier - 1) * 100 | toLocale }}% XP bonus active!
            </span>
          </h4>
        </div>
      </v-alert>

      <div v-if="loading.levels" class="py-16 d-flex justify-center grey900">
        <v-progress-circular size="32" indeterminate color="primary500" />
      </div>
      <AccountLevels
        v-else
        :xp="user.xp"
        :config="levelRewardsConfig"
        :rewards="rewards"
        :claimed-levels="user.claimedLevelRewards"
        class="grey900"
      />
    </section>

    <section v-if="claimableRewards.length" class="mb-6">
      <v-alert color="green800" tile>
        <div class="d-flex flex-wrap align-center justify-space-between">
          <div class="">
            <h4 class="text-body-1 text-uppercase font-weight-bold">
              You have {{ claimableRewards.length }} claimable rewards
            </h4>
            <p class="green100--text text-body-2 mb-0">
              Claim them before they expire!
            </p>
          </div>
          <v-btn
            color="green300 green800--text" class="font-weight-bold"
            tile :ripple="false" depressed
            @click="$modal.open('rewards', {maxWidth:500})"
          >
            View rewards
          </v-btn>
        </div>
      </v-alert>
    </section>

    <section class="mb-6">
      <v-btn
        block :ripple="false" tile
        depressed
        color="grey800 blue300--text"
        class="text-caption"
        @click="$modal.open('refcode', { maxWidth: 400 })"
      >
        <fai :icon="['fad', 'gift-card']" class="mr-1" />
        {{ user.affiliateOf ? 'Change referral code' : 'Redeem referral code' }}
      </v-btn>
    </section>


    <!-- <v-divider class="my-6" /> -->

    <section class="mb-6">
      <h3 v-t="'page.account.account.stats'" class="text-h6 text-uppercase font-weight-bold mb-1">
        <!-- TODO refresh button -->
      </h3>
      <v-row dense>
        <v-col cols="12" sm="6">
          <v-sheet color="grey800" class="pa-4 text-uppercase full-height">
            <h4 class="text-h5 green500--text lh-1 mb-1">
              {{ stats.deposited | toScrap }}
            </h4>
            <h2 v-t="'page.account.account.deposited'" class="text-body-2 font-weight-semibold grey200--text lh-1" />
          </v-sheet>
        </v-col>
        <v-col cols="12" sm="6">
          <v-sheet color="grey800" class="pa-4 text-uppercase full-height">
            <h4 class="text-h5 green500--text lh-1 mb-1">
              {{ stats.bet | toScrap }}
            </h4>
            <h2 v-t="'page.account.account.bet'" class="text-body-2 font-weight-semibold grey200--text lh-1" />
          </v-sheet>
        </v-col>
        <v-col cols="12" sm="6">
          <v-sheet color="grey800" class="pa-4 text-uppercase full-height">
            <h4 class="text-h5 green500--text lh-1 mb-1">
              {{ stats.won | toScrap }}
            </h4>
            <h2 v-t="'page.account.account.won'" class="text-body-2 font-weight-semibold grey200--text lh-1" />
          </v-sheet>
        </v-col>
        <v-col cols="12" sm="6">
          <v-sheet color="grey800" class="pa-4 text-uppercase full-height">
            <h4 class="text-h5 green500--text lh-1 mb-1">
              {{ stats.withdraw | toScrap }}
            </h4>
            <h2 v-t="'page.account.account.withdraw'" class="text-body-2 font-weight-semibold grey200--text lh-1" />
          </v-sheet>
        </v-col>
      </v-row>
    </section>

    <section class="mb-6">
      <v-row>
        <v-col v-for="(game, gameName) in gameStats" :key="gameName" cols="12" md="6">
          <h4 class="text-body-1 text-uppercase mb-2 d-flex align-center">
            <game-icon :game="gameName" class="mr-2 flex-grow-0" width="24" />
            <span v-t="`common.games.${gameName}`" class="lh-1 grey100--text font-weight-semibold" />
          </h4>

          <v-row dense>
            <v-col cols="12">
              <v-sheet color="grey800" class="px-4 py-3 full-height d-flex align-center">
                <h2 v-t="'page.account.account.total_bet'" class="text-body-1 font-weight-semibold grey200--text lh-1" />
                <v-spacer />
                <scrap :amount="gameStats[gameName].bet" class="text-body-2" />
              </v-sheet>
            </v-col>
            <v-col cols="12">
              <v-sheet color="grey800" class="px-4 py-3 full-height d-flex align-center">
                <h2 v-t="'page.account.account.total_won'" class="text-body-1 font-weight-semibold grey200--text lh-1" />
                <v-spacer />
                <scrap :amount="gameStats[gameName].won" class="text-body-2" />
              </v-sheet>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </section>

    <v-divider class="my-6" />

    <!-- self lock -->
    <section>
      <AccountSelfLock
        :loading="loading.selfLock"
        @submit="onSelfLock"
      />
    </section>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import AccountSelfLock from './components/AccountSelfLock'
import AccountLevels from './components/AccountLevels'

export default {
  components: {
    AccountLevels,
    AccountSelfLock,
  },
  data() {
    return {
      loading: {
        selfLock: false,
        levels: true,
      },
      depositXpBonusActive: false,
    }
  },
  computed: {
    ...mapGetters({
      rewards: 'user/rewards',
      user: 'user/session',
      conga: 'conga/conga',
    }),
    settings: {
      get() {
        return this.$store.getters['settings/settings']
      },
      set(val) {
        this.setSettings(val)
      },
    },
    stats() {
      const { stats } = this.user

      return {
        deposited: stats?.depositSum ?? 0,
        bet: stats?.betSum ?? 0,
        won: stats?.winSum ?? 0,
        withdraw: stats?.withdrawSum ?? 0,
      }
    },
    gameStats() {
      const { stats } = this.user

      return {
        wheel: {
          bet: stats?.games?.wheel?.betSum ?? 0,
          won: stats?.games?.wheel?.winSum ?? 0,
        },
        spinners: {
          bet: stats?.games?.spinners?.betSum ?? 0,
          won: stats?.games?.spinners?.winSum ?? 0,
        },
        coinflip: {
          bet: stats?.games?.coinflip?.betSum ?? 0,
          won: stats?.games?.coinflip?.winSum ?? 0,
        },
        cases: {
          bet: stats?.games?.cases?.betSum ?? 0,
          won: stats?.games?.cases?.winSum ?? 0,
        },
        dice: {
          bet: stats?.games?.dice?.betSum ?? 0,
          won: stats?.games?.dice?.winSum ?? 0,
        },
        caseBattles: {
          bet: stats?.games?.caseBattles?.betSum ?? 0,
          won: stats?.games?.caseBattles?.winSum ?? 0,
        },
      }
    },
    levelRewardsConfig() {
      return this.conga.promo.levelRewards
    },
    claimableRewards() {
      return this.rewards
        .filter(i => ['scrap', 'items'].includes(i.type))
    },
    depositConfig() {
      return this.conga.deposits
    },
  },
  created() {
    this.getUserInfo().catch(console.error)
    this.getUserRewards().catch(console.error)

    this.getDepositXpBonus()

    // artificial delay to avoid freeze
    setTimeout(() => {
      this.loading.levels = false
    }, 600)
  },
  methods: {
    ...mapActions({
      getUserInfo: 'user/getInfo',
      getUserRewards: 'user/getUserRewards',
      logout: 'user/logout',
    }),
    async onSelfLock(form) {
      if (this.loading.selfLock) return
      this.loading.selfLock = true

      try {
        await this.$socket.request('user.selfLock', form)
        this.$toast.success(this.$t('page.account.settings.self_lock.toast'))
      } catch (error) {
        this.$toast.error(error.message)
      } finally {
        this.loading.selfLock = false
      }
    },
    async getDepositXpBonus() {
      try {
        this.depositXpBonusActive = await this.$socket.request('user.depositXpBonusActive')
      } catch (error) {
        this.$toast.error(error.message)
      }
    },
    onLogout() {
      this.$emit('close')
      this.logout()
    },
  },
}
</script>

<style lang="scss" scoped>
.user-avatar {
  overflow: visible;
  transform: rotate(-5deg);

  &::before {
    position: absolute;
    top: 6px;
    left: 6px;
    width: 100%;
    height: 100%;
    background: primary();
    content: ' ';
  }
}

</style>
